import { format } from "date-fns";
import { MapPin, Calendar, Users, Coins, Pencil, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CircleEvent } from "@/types/circle";

interface CircleEventCardProps {
  event: CircleEvent & { spots_remaining: number };
  isOwnEvent?: boolean;
  isBooked?: boolean;
  isCircleMember?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onCancelBooking?: () => void;
}

export function CircleEventCard({ event, isOwnEvent, isBooked, isCircleMember, onEdit, onDelete, onCancelBooking }: CircleEventCardProps) {
  const startDate = new Date(event.start_time);
  
  // Handle lead data - Supabase may return it as an array from joins
  const leadData = Array.isArray(event.host) ? event.host[0] : event.host;
  const leadName = leadData 
    ? `${leadData.first_name || ""} ${leadData.last_name || ""}`.trim() || "Community Lead"
    : "Community Lead";
  const leadInitials = leadName.split(" ").map(n => n[0]).join("").toUpperCase();

  const isFull = event.spots_remaining <= 0;
  const isPending = event.status === "pending";
  const isRejected = event.status === "rejected";

  const handleEditClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onEdit?.();
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDelete?.();
  };

  const cardContent = (
    <Card className={`overflow-hidden hover:shadow-md transition-shadow border-violet-200/50 hover:border-violet-300 ${isPending || isRejected ? "opacity-90" : ""} group relative`}>
      {/* Hover-to-reveal edit/delete buttons - matching EventCardNew style */}
      {isOwnEvent && onEdit && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity bg-background/80 hover:bg-background z-20"
          onClick={handleEditClick}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      )}
      {isOwnEvent && (isPending || isRejected) && onDelete && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 right-10 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity bg-destructive/80 hover:bg-destructive text-destructive-foreground z-20"
          onClick={handleDeleteClick}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      )}
      <div className="relative">
        {event.image_url ? (
          <img
            src={event.image_url}
            alt={event.title}
            className="w-full h-32 object-cover"
          />
        ) : (
          <div className="w-full h-32 bg-gradient-to-br from-violet-100 to-violet-200 flex items-center justify-center">
            <Users className="w-10 h-10 text-violet-400" />
          </div>
        )}
        <div className="absolute top-2 left-2 flex gap-2">
          {isPending && (
            <Badge className="bg-amber-500 hover:bg-amber-500">Under Review</Badge>
          )}
          {isRejected && (
            <Badge className="bg-destructive hover:bg-destructive">Rejected</Badge>
          )}
          {isBooked && isCircleMember && !isOwnEvent && !isPending && !isRejected && (
            <Badge className="bg-emerald-500 hover:bg-emerald-500">Booked</Badge>
          )}
          {isOwnEvent && !isPending && !isRejected && (
            <Badge className="bg-violet-500">Leading</Badge>
          )}
          {isFull && !isBooked && !isPending && !isRejected && (
            <Badge className="bg-destructive">Full</Badge>
          )}
        </div>
      </div>
      <CardContent className="p-4 space-y-3">
        <h3 className="font-semibold text-foreground line-clamp-1">{event.title}</h3>
        
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="w-4 h-4 text-violet-500" />
          <span>{format(startDate, "EEE, MMM d · h:mm a")}</span>
        </div>
        
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <MapPin className="w-4 h-4 text-violet-500" />
          <span className="line-clamp-1">{event.venue}</span>
        </div>
        
        <div className="flex items-center justify-between pt-2 border-t border-border">
          <div className="flex items-center gap-2">
            <Avatar className="w-6 h-6">
              <AvatarImage src={leadData?.avatar_url || undefined} />
              <AvatarFallback className="text-xs bg-violet-100 text-violet-600">
                {leadInitials}
              </AvatarFallback>
            </Avatar>
            <span className="text-xs text-muted-foreground">{leadName}</span>
          </div>
          
          <div className="flex items-center gap-3 text-xs">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Users className="w-3.5 h-3.5" />
              <span>{event.spots_remaining} left</span>
            </div>
            <div className="flex items-center gap-1 text-violet-600 font-medium">
              <Coins className="w-3.5 h-3.5" />
              <span>{event.credit_cost}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Don't link pending or rejected events since they're not viewable yet
  if (isPending || isRejected) {
    return <div className="cursor-default">{cardContent}</div>;
  }

  return (
    <Link to={`/circle/${event.id}`}>
      {cardContent}
    </Link>
  );
}